// src/contexts/UserContext.tsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';

type User = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  tenant_id: number;
  tenant_name: string;
  tel_num?: string | null;
  has_gpt_settings: boolean;
  theme?: 'dark' | 'light';
  is_admin?: boolean;
};

type UserContextType = {
  user: User | null;
  setUser: (user: User | null) => void;
  loading: boolean;
  fetchUser: () => Promise<void>;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const fetchUser = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/me`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('ユーザー情報取得エラー:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const noAuthPaths = ['/login', '/signup'];
    if (noAuthPaths.includes(location.pathname)) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, [location.pathname]);

  return (
    <UserContext.Provider value={{ user, setUser, loading, fetchUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within a UserProvider');
  return context;
};
